import type { L } from "./lang.ts";

/**
 * Общий словарь интерфейса.
 *
 * Здесь лежит ровно то, что встречается БОЛЬШЕ ЧЕМ НА ОДНОМ экране, и то, что
 * приезжает с сервера ключом. Прозу, которая живёт на своём экране и только там
 * (оговорка под конкретным показателем, объяснение конкретной таблицы),
 * держать здесь нельзя: подпись обязана читаться рядом с местом, где её
 * печатают, иначе следующий, кто правит расчёт, не увидит оговорку про него.
 *
 * Зачем тогда словарь вообще. Из-за согласованности терминов. «Лид», «сделка»,
 * «депозит», «конверсия» встречаются на восьми экранах, и если каждый экран
 * переводит их сам, то через месяц на одном «оплаченный депозит», на другом
 * «внесённый аванс», а в справочнике третье. У нас уже есть единый словарь
 * ПОКАЗАТЕЛЕЙ (`content/metricsReference.ts`); это его продолжение для слов
 * интерфейса.
 *
 * ЧТО ЗДЕСЬ НЕ ПОЯВИТСЯ НИКОГДА:
 *   • названия стадий Битрикса — зеркало CRM, переводить нельзя;
 *   • имена парков, брендов, менеджеров, названия кампаний — данные;
 *   • коды валют.
 */

/* ------------------------------------------------------------------- экраны
 *
 * Название и подзаголовок экрана лежат ЗДЕСЬ, а не в базе, хотя в
 * `meta.app_screen` есть колонки `title` и `description`. Причина: те колонки
 * описывают ПРАВО доступа (что откроется, если выдать) и читаются в списке
 * прав; а тут — название места в интерфейсе. Раньше это было одно и то же
 * слово в двух местах, и миграция 058 переписывала базу ровно потому, что
 * подпись приезжала оттуда и при вычитке фронтенда её было не видно.
 */
export const SCREEN = {
  digest: {
    title: { ru: "Утренний дайджест", en: "Daily digest" },
    sub: {
      ru: "Что сдвинулось за сутки и на что стоит взглянуть до планёрки",
      en: "The last full day, against the same weekday a week earlier",
    },
  },
  overview: {
    title: { ru: "Обзор", en: "Overview" },
    sub: { ru: "Лиды, воронка, источники и сводка выручки", en: "Leads, the funnel, sources and a revenue summary" },
  },
  parks: {
    title: { ru: "Парки и выручка", en: "Parks & revenue" },
    sub: { ru: "Карта парков и касса из Syrve", en: "Park map and till revenue from Syrve" },
  },
  brands: {
    title: { ru: "Бренды", en: "Brands" },
    sub: { ru: "Три бренда рядом, с переходом к парку", en: "The three brands side by side, drilling down to a park" },
  },
  pipeline: {
    title: { ru: "Конверсия воронки", en: "Pipeline conversion" },
    sub: { ru: "Конверсия по источнику, парку и менеджеру ОП", en: "Conversion by source, park and salesperson" },
  },
  operations: {
    title: { ru: "Сделки и депозиты", en: "Deals & deposits" },
    sub: { ru: "Сделки, депозиты и ближайшие праздники", en: "Deals, deposits and the events coming up" },
  },
  marketing: {
    title: { ru: "Маркетинг и реклама", en: "Marketing & ad spend" },
    sub: { ru: "Расходы, цена обращения и ROMI", en: "Ad spend, cost per inquiry and ROMI" },
  },
  sales: {
    title: { ru: "Недельные продажи", en: "Weekly sales" },
    sub: {
      ru: "Недельный отчёт отдела: воронка, конверсия, депозиты, табло менеджеров",
      en: "Weekly report for the sales team: pipeline, conversion, deposits, manager scoreboard",
    },
  },
  b2b: {
    title: { ru: "B2B", en: "B2B" },
    sub: {
      ru: "Школы и компании: обращения, воронка, выручка, предоплаты, ответ GM, мероприятия",
      en: "Schools and companies: inquiries, the pipeline, revenue, prepayments, GM replies, events",
    },
  },
  journey: {
    title: { ru: "Путь клиента", en: "Customer journey" },
    sub: {
      ru: "Что привело клиента, где он сконвертировался и как далеко дошёл",
      en: "What brought them, where they converted, how far they got",
    },
  },
  client_base: {
    title: { ru: "База клиентов", en: "Client base" },
    sub: {
      ru: "Реактивация базы: выбрать когорту и создать по ней лиды",
      en: "Base reactivation: pick a cohort and create leads from it",
    },
  },
  journey_lookup: {
    title: { ru: "Поиск клиента", en: "Client lookup" },
    sub: {
      ru: "Поиск одного клиента и его путь",
      en: "Search for one client and their path",
    },
  },
  metrics: {
    title: { ru: "Справочник показателей", en: "Metrics reference" },
    sub: { ru: "Как считается каждая цифра в системе", en: "How every number in the system is calculated" },
  },
  export: {
    title: { ru: "Выгрузка", en: "Export" },
    sub: { ru: "Выгрузка данных в CSV и XLSX", en: "Data export to CSV and XLSX" },
  },
  admin: {
    title: { ru: "Администрирование", en: "Administration" },
    sub: { ru: "Пользователи, права, источники и настройки", en: "Users, access, sources and settings" },
  },
} satisfies Record<string, { title: L; sub: L }>;

/* --------------------------------------------- описания прав доступа к экранам
 *
 * Ключ — `key` из `meta.app_screen`. Это НЕ название экрана и не его
 * подзаголовок: колонка `description` там отвечает на другой вопрос — «что
 * откроет доступ, если его выдать», — и читается в таблице прав, когда решают,
 * давать право или нет. Поэтому отдельно от `SCREEN`, а не в нём.
 *
 * Отдельно названо, где видны личные данные и где интерфейс пишет в боевую CRM:
 * именно это и решает вопрос. Формулировки перенесены из миграции 058 дословно.
 */
export const SCREEN_ACCESS = {
  digest: {
    ru: "Что сдвинулось за последние сутки и на что стоит взглянуть до планёрки.",
    en: "What moved in the last day, and what is worth a look before the stand-up.",
  },
  overview: {
    ru: "Лиды, воронка, источники и сводка выручки.",
    en: "Leads, the funnel, sources and a revenue summary.",
  },
  parks: {
    ru: "Карта парков и выручка кассы из Syrve.",
    en: "Park map and till revenue from Syrve.",
  },
  brands: {
    ru: "Три бренда рядом, с переходом к парку.",
    en: "The three brands side by side, drilling down to a park.",
  },
  pipeline: {
    ru: "Конверсия по источнику, парку и менеджеру ОП.",
    en: "Conversion by source, park and salesperson.",
  },
  operations: {
    ru: "Сделки, депозиты и ближайшие праздники. Показывает полученные деньги.",
    en: "Deals, deposits and the events coming up. Shows money taken.",
  },
  marketing: {
    ru: "Расходы на рекламу, цена обращения и ROMI. Показывает, чего стоит реклама.",
    en: "Ad spend, cost per inquiry and ROMI. Shows what advertising costs.",
  },
  sales: {
    ru: "Недельный отчёт отдела продаж: воронка, конверсия, депозиты, табло менеджеров.",
    en: "Weekly report for the sales team: pipeline, conversion, deposits, manager scoreboard.",
  },
  b2b: {
    ru: "Продажи B2B: обращения, воронка по паркам, выручка, предоплаты и остатки, ответ GM, ближайшие мероприятия. Показывает организации и полученные деньги.",
    en: "B2B sales: inquiries, the B2B pipeline by park, revenue, prepayments and balances, GM response time, upcoming school and corporate events. Shows organisations and money taken.",
  },
  journey: {
    ru: "Путь клиента: что привело, где сконвертировался, как далеко дошёл.",
    en: "The customer path: what brought them, where they converted, how far they got.",
  },
  journey_lookup: {
    ru: "Поиск одного клиента и его путь. ПОКАЗЫВАЕТ ИМЕНА И ТЕЛЕФОНЫ — личные данные.",
    en: "Search for one client and their path. SHOWS NAMES AND PHONE NUMBERS — personal data.",
  },
  client_base: {
    ru: "Реактивация базы: выбрать когорту и создать по ней лиды. ПОКАЗЫВАЕТ ЛИЧНЫЕ ДАННЫЕ И ПИШЕТ В БОЕВУЮ CRM.",
    en: "Base reactivation: pick a cohort and create leads from it. SHOWS PERSONAL DATA AND WRITES TO THE LIVE CRM.",
  },
  export: { ru: "Выгрузка данных в CSV и XLSX.", en: "Data export to CSV and XLSX." },
  metrics: {
    ru: "Как считается каждая цифра: формулы, когорты, границы доверия.",
    en: "How every figure is calculated: formulas, cohorts, limits of confidence.",
  },
} satisfies Record<string, L>;

export const NAV_GROUP = {
  morning: { ru: "Каждое утро", en: "Every morning" },
  dashboards: { ru: "Отчёты", en: "Dashboards" },
  customers: { ru: "Клиенты", en: "Customers" },
  reference: { ru: "Справочник и данные", en: "Reference & data" },
} satisfies Record<string, L>;

/* -------------------------------------------------------------- общие слова */

export const UI = {
  /* --- оболочка */
  analytics: { ru: "Аналитика", en: "Analytics" },
  administrator: { ru: "Администратор", en: "Administrator" },
  user: { ru: "Пользователь", en: "User" },
  collapse: { ru: "Свернуть", en: "Collapse" },
  collapseSidebar: { ru: "Свернуть меню", en: "Collapse sidebar" },
  expandSidebar: { ru: "Развернуть меню", en: "Expand sidebar" },
  dataUpdated: { ru: "Данные обновлены", en: "Data updated" },
  signOut: { ru: "Выйти", en: "Sign out" },

  /* --- фильтры */
  periodPreset: { ru: "Готовый период", en: "Period preset" },
  fromDate: { ru: "Дата с", en: "From date" },
  toDate: { ru: "Дата по", en: "To date" },
  monthToDate: { ru: "С начала месяца", en: "Month to date" },
  since1Jul: { ru: "С 1 июля", en: "Since 1 Jul" },
  parks: { ru: "Парки", en: "Parks" },
  brands: { ru: "Бренды", en: "Brands" },
  all: { ru: "Все", en: "All" },
  managedEra: { ru: "Рабочая эпоха", en: "Managed era" },
  allData: { ru: "Все данные", en: "All data" },
  includeLegacy: {
    ru: "Включить лиды до того, как в CRM стали вести воронку",
    en: "Include leads from before the CRM funnel was maintained",
  },
  compareToPrevious: { ru: "Сравнить с прошлым", en: "Compare to previous" },
  compareHint: {
    ru: "сравнить с предыдущим отрезком той же длины",
    en: "compare against the previous period of the same length",
  },
  comparingAgainst: { ru: "сравнение с", en: "comparing against" },
  saved: { ru: "Сохранённые", en: "Saved" },
  saveCurrentFilters: { ru: "Сохранить текущие фильтры", en: "Save current filters" },
  /* Подпись кнопки, сворачивающей панель фильтров на узком экране. */
  filters: { ru: "Фильтры", en: "Filters" },
  /* В сводке свёрнутой панели: сравнение включено. Короче, чем на самом чипе,
     потому что стоит в одной строке с парком и периодом. */
  compareOn: { ru: "со сравнением", en: "with comparison" },
  viewName: { ru: "Название набора", en: "View name" },
  save: { ru: "Сохранить", en: "Save" },
  cancel: { ru: "Отмена", en: "Cancel" },
  deleteView: { ru: "Удалить набор", en: "Delete view" },

  /* --- оформление */
  appearance: { ru: "Оформление", en: "Appearance" },
  appearanceScope: {
    ru: "тема, плотность и шрифт сохраняются только в этом браузере",
    en: "theme, density and font are saved in this browser only",
  },
  language: { ru: "Язык", en: "Language" },
  languageScope: {
    ru: "Интерфейс и справочник. Названия стадий, парков и кампаний приходят из CRM и не переводятся.",
    en: "Interface and the reference. Stage, park and campaign names come from the CRM and stay as they are.",
  },
  theme: { ru: "Тема", en: "Theme" },
  dark: { ru: "Тёмная", en: "Dark" },
  light: { ru: "Светлая", en: "Light" },
  density: { ru: "Плотность", en: "Density" },
  tight: { ru: "Плотно", en: "Tight" },
  normal: { ru: "Обычно", en: "Normal" },
  cozy: { ru: "Свободно", en: "Cozy" },
  textFont: { ru: "Шрифт текста", en: "Text font" },
  fontScope: {
    ru: "Основной текст и таблицы. Заголовки и крупные числа остаются фирменными.",
    en: "Body text and tables. Headings and the big figures keep the brand face.",
  },
  printScreen: { ru: "Распечатать экран", en: "Print this screen" },

  /* --- курс валюты */
  exchangeRates: { ru: "Курсы валют", en: "Exchange rates" },
  exchangeRatesScope: {
    ru: "Видят все. Смена курса пересчитывает все сохранённые суммы в этой валюте, за всю историю, а не только то, что придёт дальше.",
    en: "Everyone sees this. Changing a rate recalculates every stored amount in that currency, across the whole history — not only what comes in next.",
  },
  apply: { ru: "Применить", en: "Apply" },
  recalculating: { ru: "Пересчёт…", en: "Recalculating…" },
  adminCanChange: { ru: "Курс меняет администратор.", en: "An administrator can change this." },
  sameValue: { ru: "Значение то же — пересчитывать нечего.", en: "Same value — nothing to recalculate." },
  lastChange: { ru: "Последняя правка", en: "Last change" },
  wasRate: { ru: "было", en: "was" },
  amountsRecalculated: {
    ru: "сохранённых сумм пересчитано. Обновите экран, чтобы увидеть новые цифры.",
    en: "stored amounts recalculated. Reload the screen to see the new figures.",
  },
  loaderStillAt: {
    ru: "Загрузка пока считает по курсу",
    en: "The loader is still converting at",
  },
  restartToAgree: {
    ru: "— перезапустите сервер, чтобы курсы совпали.",
    en: "— restart the server so both agree.",
  },

  /* --- состояния */
  loading: { ru: "Загрузка…", en: "Loading…" },
  noData: { ru: "нет данных", en: "no data" },
  notSet: { ru: "не задано", en: "not set" },
  never: { ru: "никогда", en: "never" },
  justNow: { ru: "только что", en: "just now" },
  ago: { ru: "назад", en: "ago" },
  total: { ru: "Итого", en: "Total" },
  of: { ru: "из", en: "of" },
  showMore: { ru: "Показать ещё", en: "Show more" },
  close: { ru: "Закрыть", en: "Close" },
  retry: { ru: "Повторить", en: "Retry" },
  somethingWrong: { ru: "Не удалось загрузить", en: "Could not load" },

  /* --- два депозита, и это два разных факта
   *
   * `depositRecorded` — то, что менеджер зафиксировал в поле сделки, включая
   * доплаты. Есть с 2023 года.
   * `depositConfirmed` — платёж, зарегистрированный системой бронирования. У
   * него есть ДАТА и время «ссылка → оплата», чего у CRM нет вовсе, но он
   * знает только первый холд.
   */
  depositRecorded: { ru: "Депозиты записаны в CRM", en: "Deposits recorded in CRM" },
  depositConfirmed: { ru: "Платежи депозита подтверждены", en: "Deposit payments confirmed" },
  depositRecordedShort: { ru: "записано в CRM", en: "recorded in CRM" },
  depositConfirmedShort: { ru: "подтверждено системой", en: "confirmed by the system" },
  /*
   * Прежний текст объяснял разрыв «доплатами депозита, которые идут только в
   * CRM». Такого понятия у заказчика нет — депозит платят один раз, — и данные
   * объяснение не подтвердили: замер 13.09.2026, 155 сделок с двумя
   * заполненными депозитами, 136 совпадают точно, а из 19 расхождений у 8 поле
   * CRM равно сумме сделки, то есть в него вписали не депозит. Поэтому оговорка
   * теперь называет ПРОИСХОЖДЕНИЕ чисел, а не выдуманную механику.
   */
  depositTwoFacts: {
    ru: "Это два разных факта, а не два измерения одного. Первое — деньги, которые увидела система бронирования; второе — число, которое менеджер вписал в карточку сделки. Совпадают они почти всегда, а когда расходятся, в поле CRM обычно стоит сумма сделки, а не депозит.",
    en: "These are two different facts, not two measurements of one. The first is money the booking system actually saw; the second is a number a salesperson typed into the deal. They agree almost always, and where they do not, the CRM field usually holds the deal amount rather than the deposit.",
  },
  bookingStartsHere: {
    ru: "система бронирования начинается здесь",
    en: "the booking system starts here",
  },
  beforeBookingSystem: {
    ru: "До запуска системы бронирования подтверждённых платежей не существовало нигде — здесь прочерк, а не ноль.",
    en: "Before the booking system went live no confirmed payment existed anywhere — a dash, not a zero.",
  },

  /* --- термины предметной области */
  leads: { ru: "Лиды", en: "Leads" },
  /**
   * Обращения — ВСЕ касания клиента, а не квалифицированные лиды.
   *
   * Разведено по требованию заказчика на сдаче: «это не лид, это просто контакт
   * с нами», «не лидс, а conversations». Под словом «лид» у отдела продаж
   * понимается обращение по дню рождения, дошедшее до менеджера; счёт всех
   * касаний подряд включает вопросы про часы работы, резюме и звонки не по теме,
   * и называть его лидами значит завышать поток в несколько раз. Замер на экране
   * брендов: 9 425 обращений против 310 с признаком дня рождения.
   *
   * `leads` остаётся там, где речь о КОНКРЕТНОМ лиде: в списке лидов, в карточке
   * сделки, в пути клиента.
   *
   * 29.09.2026 — «Total inquiries» / «Всего обращений» по требованию заказчика:
   * «Conversations» путали с диалогами WhatsApp из кабинета Meta, а это другое
   * число. Диалоги Meta так и называются диалогами (экран маркетинга).
   */
  conversations: { ru: "Всего обращений", en: "Total inquiries" },
  lead: { ru: "Лид", en: "Lead" },
  deals: { ru: "Сделки", en: "Deals" },
  deal: { ru: "Сделка", en: "Deal" },
  won: { ru: "Выиграно", en: "Won" },
  lost: { ru: "Проиграно", en: "Lost" },
  revenue: { ru: "Выручка", en: "Revenue" },
  deposit: { ru: "Депозит", en: "Deposit" },
  deposits: { ru: "Депозиты", en: "Deposits" },
  conversion: { ru: "Конверсия", en: "Conversion" },
  source: { ru: "Источник", en: "Source" },
  sources: { ru: "Источники", en: "Sources" },
  park: { ru: "Парк", en: "Park" },
  brand: { ru: "Бренд", en: "Brand" },
  stage: { ru: "Стадия", en: "Stage" },
  manager: { ru: "Менеджер", en: "Manager" },
  salesperson: { ru: "Менеджер ОП", en: "Salesperson" },
  campaign: { ru: "Кампания", en: "Campaign" },
  spend: { ru: "Расход", en: "Spend" },
  tickets: { ru: "Билеты", en: "Tickets" },
  guests: { ru: "Гости", en: "Guests" },
  receipts: { ru: "Чеки", en: "Receipts" },
  events: { ru: "Праздники", en: "Events" },
  week: { ru: "Неделя", en: "Week" },
  day: { ru: "День", en: "Day" },
  days: { ru: "дней", en: "days" },
  perDay: { ru: "в день", en: "per day" },
  median: { ru: "Медиана", en: "Median" },
  share: { ru: "Доля", en: "Share" },
} satisfies Record<string, L>;

/* ---------------------------------------------------- подписи из базы: каналы
 *
 * Ключ — `channel_key` из `core.dim_channel`. Английская подпись в базе
 * остаётся: по ней работают выгрузки и она же запасной вариант.
 */
export const CHANNEL = {
  whatsapp: { ru: "WhatsApp", en: "WhatsApp" },
  call: { ru: "Звонки", en: "Calls" },
  walkin: { ru: "Пришёл сам", en: "Walk-in" },
  repeat: { ru: "Повторные продажи — реактивация базы", en: "Repeat sales — reactivated base" },
  manual: { ru: "Заведён менеджером", en: "Entered by a manager" },
  google_organic: { ru: "Карточка в Google Maps", en: "Google Maps listing" },
  webform: { ru: "Формы CRM", en: "CRM forms" },
  site: { ru: "Сайт", en: "Website" },
  ticketing: { ru: "Сайт билетов", en: "Ticket site" },
  meta_ads: { ru: "Реклама Meta", en: "Meta Ads" },
  google_ads: { ru: "Реклама Google", en: "Google Ads" },
  meta_organic: { ru: "Meta / Instagram, без рекламы", en: "Meta / Instagram organic" },
  mall_ads: { ru: "Реклама в ТЦ", en: "Mall advertising" },
  email: { ru: "E-mail", en: "E-mail" },
  referral: { ru: "По рекомендации", en: "Referral" },
  loyalty: { ru: "Программы лояльности", en: "Loyalty programmes" },
  other: { ru: "Другое", en: "Other" },
  unknown: { ru: "Источник не определён", en: "Source not identified" },
} satisfies Record<string, L>;

/* -------------------------------------------- подписи из базы: что не сходится
 *
 * Ключ — `issue` из `api/operations.ts`.
 */
export const ATTENTION = {
  deposit_over_amount: { ru: "Депозит больше суммы сделки", en: "Deposit larger than the deal amount" },
  deposit_on_lost: { ru: "Депозит взят, сделка проиграна", en: "Deposit taken, deal marked lost" },
  open_past_event: { ru: "Праздник прошёл, сделка открыта", en: "Event has passed, deal still open" },
  won_without_deposit: { ru: "Выиграна, но депозит не записан", en: "Won, but no deposit recorded" },
  won_without_amount: { ru: "Выиграна, но сумма пустая", en: "Won, but the amount is empty" },
  no_event_date: { ru: "Нет даты праздника", en: "No event date" },
} satisfies Record<string, L>;

/* ---------------------------------- подписи из базы: возраст лидов в работе */
export const AGE_BUCKET = {
  "0_7": { ru: "0–7 дней", en: "0-7 days" },
  "8_14": { ru: "8–14 дней", en: "8-14 days" },
  "15_30": { ru: "15–30 дней", en: "15-30 days" },
  over_30: { ru: "больше 30 дней", en: "over 30 days" },
} satisfies Record<string, L>;

/* ------------------------------------- подписи из базы: ступени пути клиента */
export const JOURNEY_STEP = {
  enquiry: { ru: "Обращение", en: "Inquiry" },
  /* Ступень пути клиента: цикл дошёл до Interested / Park Tour ИЛИ закончился праздником —
     то же определение, что у плитки «Квалифицированные лиды» на Обзоре. */
  qualified: { ru: "Квалифицированный лид", en: "Qualified lead" },
  deal: { ru: "Создана сделка", en: "Deal created" },
  paid: { ru: "Деньги получены", en: "Money received" },
  happened: { ru: "Банкет прошёл", en: "Banquet happened" },
} satisfies Record<string, L>;

/* ------------------------------------------ подписи из базы: откуда метка UTM
 *
 * Ключ — `basis_key` из `api/dashboard.ts`. Формулировки намеренно называют
 * ПРОИСХОЖДЕНИЕ метки, а не заполненность полей: метку, которую записали мы
 * сами, нельзя показывать как разметку клиента.
 */
export const UTM_BASIS = {
  client_tag: { ru: "метка кампании от клиента", en: "campaign tag from the client" },
  analytics_tag: { ru: "метку записала аналитика", en: "tag written by analytics" },
  site_ref: { ru: "метка с сайта бренда через WhatsApp", en: "tag from a brand website via WhatsApp" },
  chat_link: { ru: "восстановлено из чата, в CRM не записано", en: "restored from chat, not written back" },
  no_platform: { ru: "метка есть, но площадку не называет", en: "tag present but names no platform" },
  no_tag: { ru: "метки нет", en: "no tag" },
} satisfies Record<string, L>;

/* ------------------------------------------------- подписи из базы: выгрузки */
export const DATASET = {
  leads: {
    title: { ru: "Лиды", en: "Leads" },
    note: {
      ru: "по строке на лид: стадия, парк, канал, менеджер, квалифицированный ли лид и стал ли сделкой",
      en: "one row per lead: stage, park, channel, manager, whether it is a qualified lead and became a deal",
    },
  },
  deals: {
    title: { ru: "Сделки", en: "Deals" },
    note: {
      ru: "банкетная воронка: сумма, депозит, стадия, дата события. Деньги в валюте парка и в AED",
      en: "the banquet pipeline: amount, deposit, stage, event date. Money in both the park currency and AED",
    },
  },
  deposits: {
    title: { ru: "Депозиты и платежи", en: "Deposits & payments" },
    note: {
      ru: "брони из системы бронирования: ссылка отправлена, оплачено и сколько прошло между",
      en: "bookings from the booking system: link sent, paid, and how long it took in between",
    },
  },
  "park-revenue": {
    title: { ru: "Выручка парков по дням", en: "Park revenue by day" },
    note: {
      ru: "касса Syrve по дням и группам (билеты, еда, банкеты, прочее); строка «не разнесено» — чеки без типа оплаты",
      en: "Syrve till revenue by day and group (tickets, food, banquets, other); the unallocated row is receipts with no payment type",
    },
  },
  "online-tickets": {
    title: { ru: "Онлайн-билеты по дням", en: "Online tickets by day" },
    note: {
      ru: "продажи с нашего сайта билетов: оплаченные брони, билеты, скидки, купоны",
      en: "sales from our ticket site: paid bookings, tickets, discounts, coupons",
    },
  },
  "ad-spend": {
    title: { ru: "Реклама по кампаниям", en: "Ad spend by campaign" },
    note: {
      ru: "расход Meta по кампании и дню, с парком и способом разнесения, плюс начатые переписки WhatsApp",
      en: "Meta spend per campaign per day, with the park it was attributed to and how — plus WhatsApp conversations started",
    },
  },
  channels: {
    title: { ru: "Каналы привлечения", en: "Acquisition channels" },
    note: {
      ru: "по каналу: всего обращений, квалифицированные лиды, сделки, выигранные сделки и конверсии",
      en: "per channel: total inquiries, qualified leads, deals, won deals and conversion rates",
    },
  },
  "b2b-deals": {
    title: { ru: "Сделки B2B", en: "B2B deals" },
    note: {
      ru: "воронка B2B: по строке на сделку, созданную или проводимую в периоде, — парк, канал, стадия, сумма, предоплата, остаток, первый ответ GM, эскалация",
      en: "the B2B pipeline: one row per deal created or held in the period — park, channel, stage, amount, prepayment, balance, GM first reply, escalation",
    },
  },
} satisfies Record<string, { title: L; note: L }>;

/* ------------------------------------------ подписи из базы: названия валют
 *
 * Ключ — код валюты. Сам КОД не переводится нигде: AED и BHD это международные
 * обозначения, и в отчёте, который читают в Дубае и в Манаме, они обязаны
 * выглядеть одинаково. Переводится только человеческое название рядом с кодом.
 */
export const CURRENCY = {
  AED: { ru: "дирхам ОАЭ", en: "UAE dirham" },
  BHD: { ru: "бахрейнский динар", en: "Bahraini dinar" },
  USD: { ru: "доллар США", en: "US dollar" },
} satisfies Record<string, L>;

/* ------------------------------- подписи из базы: вид конверсии в рекламе
 *
 * Ключ — `conversion_kind` из `mart.ad_spend_daily` (миграция 051). Различие
 * принципиально для чтения цифр: подписка на страницу и начатая переписка — это
 * не лид, и складывать их в один счётчик значило бы завышать результат рекламы.
 */
export const CONVERSION_KIND = {
  lead: { ru: "заявки", en: "leads" },
  engagement: { ru: "вовлечение", en: "engagement" },
  awareness: { ru: "охват", en: "awareness" },
  other: { ru: "прочее", en: "other" },
} satisfies Record<string, L>;

/* ------------------------------- насколько показателю можно верить
 *
 * Ключ — `badge` из блока справочника (`content/metricsReference.ts`). Это
 * главное различие того документа: «точная» и «оценка сверху» — не оттенки
 * формулировки, а разные права на вывод. Подпись нужна и справочнику, и поиску,
 * поэтому лежит здесь: разойдись они, поиск назовёт ROMI оценкой, а справочник
 * отношением, и оба будут «правы».
 */
export const BADGE = {
  exact: { ru: "точная", en: "exact" },
  estimate: { ru: "оценка сверху", en: "upper bound" },
  ratio: { ru: "отношение", en: "ratio" },
} satisfies Record<string, L>;

/* ------------------------------------------- подписи из базы: эпохи и группы */
export const ERA = {
  managed: { ru: "рабочая эпоха", en: "managed era" },
  legacy: { ru: "старая эпоха", en: "legacy era" },
  all: { ru: "обе эпохи", en: "both eras" },
} satisfies Record<string, L>;

export const REVENUE_GROUP = {
  tickets: { ru: "Билеты", en: "Tickets" },
  food: { ru: "Еда", en: "Food" },
  banquet: { ru: "Банкеты", en: "Banquets" },
  other: { ru: "Прочее", en: "Other" },
  unallocated: { ru: "Не разнесено", en: "Unallocated" },
} satisfies Record<string, L>;

/* -------------------------------------------------------------- сентинелы
 *
 * База отдаёт словами то, что означает «сопоставить не удалось». Это не
 * подпись, а признак, и по нему же идёт группировка, поэтому менять строку в
 * базе нельзя — сменился бы ключ группировки. Перевод делается на печати.
 */
export const SENTINEL = {
  "(not identified)": { ru: "(не определён)", en: "(not identified)" },
  "(не сопоставлен)": { ru: "(не сопоставлен)", en: "(not mapped)" },
  "(no park)": { ru: "(без парка)", en: "(no park)" },
  "(brand-level)": { ru: "(на уровне бренда)", en: "(brand-level)" },
  "(not set)": { ru: "(не задано)", en: "(not set)" },
  "(unassigned)": { ru: "(не назначен)", en: "(unassigned)" },
  "(тип оплаты не указан)": { ru: "(тип оплаты не указан)", en: "(no payment type)" },
} satisfies Record<string, L>;

/* ------------------------------- подписи из базы: реактивация базы клиентов
 *
 * Ключ — `reason` из `api/cohorts.ts`. Причина лежит в журнале прогонов, и у
 * строк, созданных ДО этой правки, там русская проза — она и покажется как
 * запасной вариант. Переписывать журнал ради подписи нельзя: журнал — след
 * того, что произошло, а не текст интерфейса.
 */
export const COHORT_REASON = {
  kept_previous: { ru: "работал с этим клиентом раньше", en: "worked with this client before" },
  previous_not_in_park: { ru: "прежний менеджер не ведёт этот парк", en: "the previous manager does not cover this park" },
  by_load: { ru: "распределено по загрузке", en: "assigned by workload" },
  no_active_managers: { ru: "по парку нет активных менеджеров", en: "no active managers for this park" },
} satisfies Record<string, L>;

/* ------------------------- подписи из базы: почему кандидата когорты пропустили
 *
 * Ключ — `skip_reason` из `api/cohorts.ts`. До 10.09.2026 сервер отдавал готовую
 * РУССКУЮ фразу, и она печаталась на англоязычном экране как есть — тот же
 * дефект, что был у описаний настроек и подписей корзин возраста: подпись
 * приходит из базы, поэтому при вычитке фронтенда её не видно.
 */
export const SKIP_REASON = {
  no_phone: { ru: "нет телефона", en: "no phone number" },
  already_created: {
    ru: "лид по этому клиенту уже создавали",
    en: "a lead for this client was already created",
  },
  active_lead: { ru: "уже есть активный лид", en: "an active lead already exists" },
  active_deal: { ru: "уже есть активная сделка", en: "an active deal already exists" },
  no_park: { ru: "у сделки не определён парк", en: "the deal has no park" },
} satisfies Record<string, L>;

/* -------------------- подписи из базы: почему метку не записали в Битрикс
 *
 * Ключ — `skipped_reason` из `meta.attribution_write` (см.
 * `sources/attribution/writeBack.ts`). У ключа `busy` после двоеточия идут
 * подробности — какие поля заняты и чем; их печатает интерфейс как есть.
 *
 * У записей, сделанных ДО 10.09.2026, в журнале лежит русская проза вместо
 * ключа: журнал — след того, что произошло, и переписывать его нельзя.
 * `dbLabel` покажет её как запасной вариант.
 */
export const WRITEBACK_SKIP = {
  unreadable: { ru: "лид не читается через API", en: "the lead cannot be read through the API" },
  already_ours: { ru: "значение уже такое", en: "the value is already this" },
  busy: { ru: "занято человеком", en: "filled in by a person" },
} satisfies Record<string, L>;

/* ------------------------------------------- ошибки правки настроек по коду */
export const SETTING_ERROR = {
  unknown_key: { ru: "такой настройки нет", en: "no such setting" },
  not_editable: {
    ru: "Это определение, а не эксплуатационная настройка. Оно меняет смысл цифр во всех отчётах сразу, поэтому правится миграцией: так изменение попадает в девлог и в справочник показателей рядом с формулой.",
    en: "This is a definition, not an operational setting. It changes the meaning of figures in every report at once, so it is changed by a migration — that way the change lands in the devlog and in the metrics reference next to the formula it changes.",
  },
  bad_value: { ru: "значение не подходит", en: "that value will not do" },
  /* Таблица менеджеров парков сторожа открытых линий. */
  unknown_park: { ru: "такого парка нет", en: "no such park" },
  manager_not_usable: {
    ru: "этот пользователь не подходит: нужен активный сотрудник, не бот и не служебная учётная запись",
    en: "this user will not do: it has to be an active employee, not a bot and not a service account",
  },
} satisfies Record<string, L>;

export const VALUE_KIND = {
  bool: { ru: "true или false", en: "true or false" },
  int: { ru: "целое число от 1 до 100000", en: "a whole number between 1 and 100000" },
  date: { ru: "дата в виде ГГГГ-ММ-ДД", en: "a date as YYYY-MM-DD" },
  text: { ru: "непустая строка", en: "a non-empty string" },
  enum: { ru: "одно из допустимых значений", en: "one of the allowed values" },
  idlist: {
    ru: "идентификаторы через запятую, либо пусто",
    en: "identifiers separated by commas, or empty",
  },
} satisfies Record<string, L>;

/* --------------------------------- подписи значений настроек-перечислений
 *
 * Ключ — само значение, как оно лежит в `meta.app_setting.allowed_values`.
 * Значение, которого тут нет, печатается как есть: словарь объясняет выбор, а
 * не разрешает его.
 */
export const SETTING_VALUE = {
  off: { ru: "выключен", en: "off" },
  observe: { ru: "наблюдает, ничего не пишет", en: "observing, writes nothing" },
  on: { ru: "переводит на менеджера", en: "hands over to the manager" },
  deal: { ru: "только активные сделки", en: "active deals only" },
  lead: { ru: "только активные лиды вне Inquiry", en: "active leads outside Inquiry only" },
  both: { ru: "сделки и лиды", en: "deals and leads" },
} satisfies Record<string, L>;

/* ----------------------------------- что именно отсекла настройка области
 *
 * Ключ — вид НАЙДЕННОЙ сущности, а область в подписи названа противоположная:
 * нашли лид, а проверяем только сделки. Одной фразы «вне выбранной области»
 * недостаточно — по ней не понять ни что нашли, ни что менять.
 */
export const WATCHDOG_SCOPE_MISS = {
  lead: {
    ru: "подходит лид, а проверяем только сделки",
    en: "a lead fits, but only deals are checked",
  },
  deal: {
    ru: "подходит сделка, а проверяем только лиды",
    en: "a deal fits, but only leads are checked",
  },
} satisfies Record<string, L>;

/* ------------------------------------------------ исход проверки одного диалога
 *
 * «Ответили сами» и «активной сделки нет» — не шум, а объяснение: именно они
 * отвечают на вопрос «почему этому клиенту не перевели». Рубильник без такого
 * объяснения через неделю выключают обратно как неработающий.
 */
export const WATCHDOG_VERDICT = {
  transferred: { ru: "переведено на менеджера", en: "handed to the manager" },
  would_transfer: { ru: "перевёл бы (наблюдение)", en: "would hand over (observing)" },
  answered: { ru: "ответили сами", en: "somebody replied" },
  service_owner: {
    ru: "ответственный — служебная учётная запись",
    en: "owner is a service account",
  },
  no_target: { ru: "активной сделки и лида нет", en: "no active deal or lead" },
  out_of_scope: {
    ru: "активная сущность есть, но вне выбранной области проверки",
    en: "an active record exists, but outside the chosen scope",
  },
  failed: { ru: "перевести не удалось", en: "hand-over failed" },
} satisfies Record<string, L>;

/* --------------------------------------- описания эксплуатационных настроек
 *
 * Ключ — `key` из `meta.app_setting`. До двуязычия описания приезжали из базы и
 * были ПО-РУССКИ — то есть на английском экране Administration стоял русский
 * текст. Тот же класс дефекта, что нашёлся в миграциях 057 и 058: подпись
 * приходит из базы, поэтому при вычитке фронтенда её не видно.
 *
 * Описание из базы остаётся запасным вариантом: настройка, которую завели
 * миграцией и забыли добавить сюда, покажет текст из базы, а не пустоту.
 */
export const SETTING = {
  ad_spend_lookback_days: {
    ru: "Сколько последних дней перезабирать у рекламных кабинетов: атрибуция уточняется задним числом.",
    en: "How many recent days to re-pull from the ad accounts: attribution keeps being refined after the fact.",
  },
  attribution_lookback_days: {
    ru: "На сколько дней назад искать источник лида.",
    en: "How far back to look for a lead's source.",
  },
  attribution_max_leads_per_run: {
    ru: "Сколько лидов разбирать за прогон. Очередь клиента Битрикса общая: длинный прогон ставит в ожидание все остальные источники.",
    en: "How many leads to process per run. The Bitrix client queue is shared: a long run makes every other source wait.",
  },
  attribution_max_resolves_per_run: {
    ru: "Сколько коротких ссылок разворачивать за прогон: редирект плюс запрос в Graph — самая дорогая операция, а коды fb.me выдаются на каждый шаринг, поэтому кеш спасает лишь частично.",
    en: "How many short links to expand per run: a redirect plus a Graph call is the most expensive operation here, and fb.me codes are minted per share, so caching only helps so much.",
  },
  attribution_max_seconds_per_run: {
    ru: "Потолок по времени. Меньше таймаута планировщика (1200 с), чтобы прогон завершался сам, а не бросался недоделанным.",
    en: "A time ceiling. Below the scheduler timeout (1200 s) so a run finishes on its own instead of being abandoned half-done.",
  },
  attribution_write_enabled: {
    ru: "Записывать ли найденный источник обратно в лид Битрикса.",
    en: "Whether to write the source we found back onto the Bitrix lead.",
  },
  attribution_qualify_enabled: {
    ru: "Переводить из INQUIRY в Qualified лиды, у которых ссылка в первом сообщении уверенно связана с объявлением Meta, — те же лиды, что получают метку meta / cpc-inferred. Свежий лид переезжает за 5–10 минут, метка по-прежнему пишется раз в час. Только с INQUIRY: лид, которого менеджер уже повёл дальше или закрыл, не трогается. При включении переедут и старые лиды со ссылкой, которые сейчас лежат на INQUIRY.",
    en: "Move from INQUIRY to Qualified the leads whose first message carries a link confidently matched to a Meta ad — the same leads that get the meta / cpc-inferred tag. A fresh lead moves within 5–10 minutes; the tag is still written hourly. Only from INQUIRY: a lead a salesperson has already moved on or closed is left alone. Switching it on also moves older leads with a link that are sitting on INQUIRY now.",
  },
  attribution_google_starter_enabled: {
    ru: "Ставить метку google / cpc-inferred лидам WhatsApp, чьё первое сообщение — стартовый текст кнопки WhatsApp из объявлений Google. Ссылки на объявление в таком чате нет, и кампанию по тексту не определить: он один на кабинет, поэтому поле кампании остаётся пустым. Пишется только вместе с общей записью источника; в Qualified такие лиды не переводятся.",
    en: "Tag as google / cpc-inferred the WhatsApp leads whose first message is the starter text of the WhatsApp button in Google ads. Such a chat carries no link to the ad, and the text cannot name the campaign — it is one per account — so the campaign field stays empty. Written only together with the general source write-back; such leads are not moved to Qualified.",
  },
  attribution_site_ref_enabled: {
    ru: "Ставить метку лидам WhatsApp, чьё первое сообщение несёт код сайта бренда «Ref: LL google/cpc/…» — его добавляет кнопка WhatsApp на leoloona.ae, zamania.ae и hellokids.ae. Источник, канал и кампания пишутся как их снял сайт с перехода посетителя, в utm_content — сайт. Только в пустые поля, вместе с общей записью источника; в Qualified такие лиды не переводятся.",
    en: "Tag the WhatsApp leads whose first message carries a brand website code “Ref: LL google/cpc/…” — the WhatsApp button on leoloona.ae, zamania.ae and hellokids.ae adds it. The source, medium and campaign are written as the site took them from the visitor's arrival, with the site in utm_content. Empty fields only, together with the general source write-back; such leads are not moved to Qualified.",
  },
  attribution_starter_from: {
    ru: "С какой даты лиды без ссылки перечитываются на стартовый текст кнопки WhatsApp, когда он появляется или меняется. Первые чаты из кнопки в объявлениях Google — декабрь 2025.",
    en: "From which date leads without a link are re-read for the WhatsApp button's starter text when that text appears or changes. The first chats from the button in Google ads date from December 2025.",
  },
  attribution_write_max_per_run: {
    ru: "Сколько лидов размечать в Битриксе за прогон. Ограничение не в темпе вызовов, а в операционном бюджете портала: измерено, что 355 правок подряд выбирают его целиком.",
    en: "How many leads to tag in Bitrix per run. The limit is not the call rate but the portal's operating budget: 355 consecutive edits were measured to use it up entirely.",
  },
  cohort_max_leads_per_run: {
    ru: "Сколько лидов создавать за одно нажатие в реактивации базы — и за один автозапуск 1-го числа. Ограничение рабочее: больше отдел не разберёт.",
    en: "How many leads one click creates in base reactivation — and one auto-run on the 1st. The limit is operational: the team cannot work through more.",
  },
  cohort_auto_monthly: {
    ru: "Реактивация базы сама: 1-го числа в 09:00 по Дубаю создаёт лиды по клиентам, праздновавшим в этом месяце год назад, по тем же правилам, что кнопка на экране Client base. Включение посреди месяца сразу ничего не создаёт — первый запуск 1-го следующего месяца.",
    en: "Base reactivation on its own: on the 1st at 09:00 Dubai time it creates leads for clients who celebrated in that month a year ago, by the same rules as the button on the Client base screen. Switching on mid-month creates nothing straight away — the first run is on the 1st of the next month.",
  },
  ol_response_max_sessions_per_run: {
    ru: "Сколько диалогов открытых линий разбирать за прогон — новых и перечитываемых вместе. Один запрос к порталу на диалог.",
    en: "How many open-line dialogues to read per run — new and re-read ones together. One portal call per dialogue.",
  },
  ol_response_backfill_days: {
    ru: "На сколько дней назад догонять историю диалогов при первом прогоне.",
    en: "How many days of dialogue history the first run catches up on.",
  },
  ol_response_recheck_days: {
    ru: "Сколько дней перечитывать диалоги, оставшиеся без ответа: ответ, данный позже первого разбора, иначе в отчёт не попадёт. Совпадает с окном карточки «Никто не ответил».",
    en: "For how many days to re-read dialogues left without a reply: otherwise a reply given after the first read never reaches the report. Matches the window of the “Nobody replied” card.",
  },
  ol_watchdog_mode: {
    ru: "Сторож открытых линий. Если клиенту с активной сделкой не ответил никто — ни бот, ни человек, — переводит диалог на ответственного менеджера и пишет об этом в таймлайн. В положении «наблюдает» разбирает настоящие диалоги и записывает свои решения, но в Битрикс не пишет ничего.",
    en: "The open-line watchdog. When nobody — neither the bot nor a person — has replied to a client with an active deal, it hands the dialog to the responsible manager and writes a note in the timeline. Set to “observing” it judges real dialogs and records its decisions but writes nothing into Bitrix.",
  },
  ol_watchdog_scope: {
    ru: "Где искать, кому отдать диалог: в активных сделках, в активных лидах вне Inquiry, или в обоих. Стадия Inquiry не проверяется никогда — там работает бот.",
    en: "Where to look for whom to hand the dialog to: active deals, active leads outside Inquiry, or both. The Inquiry stage is never considered — that is the bot's ground.",
  },
  ol_watchdog_wait_minutes: {
    ru: "Сколько минут даём боту ответить, прежде чем считать диалог брошенным. Это и есть граница между первой линией бота и страховкой.",
    en: "How many minutes the bot is given to reply before the dialog counts as abandoned. This is the line between the bot's first-line work and the safety net.",
  },
  ol_watchdog_lookback_hours: {
    ru: "За сколько часов назад смотреть новые диалоги. Решение по диалогу принимается один раз, поэтому окно влияет только на то, как далеко сторож догоняет после простоя.",
    en: "How far back to look for new dialogs. Each dialog is judged once, so the window only decides how far back the watchdog catches up after downtime.",
  },
  ol_watchdog_service_accounts: {
    ru: "Служебные учётные записи: диалог им не отдаём. Брошенный на них ЛИД сторож отдаёт менеджеру парка из таблицы ниже — передаёт диалог и ставит ответственным за лид; без менеджера для парка остаётся предупреждение. 75289 — Suvvy, её ставят ответственной за первые лиды, поэтому молчание на ней значит «бот обязан был ответить и не ответил». 1 — технический аккаунт интегратора, лиды на нём оказались по недосмотру.",
    en: "Service accounts: they never receive a dialog. An abandoned LEAD sitting on one goes to the park manager from the table below — the dialog is handed over and the manager becomes the lead's owner; with no manager for the park it stays a warning. 75289 is Suvvy, set as the owner of first leads — so silence there means the bot should have replied and did not. 1 is the integrator's technical account; leads landed on it by mistake.",
  },
  ol_watchdog_max_transfers_per_run: {
    ru: "Потолок переводов за прогон: первое включение не должно превратиться в сотню переводов подряд.",
    en: "A ceiling on hand-overs per run: switching this on for the first time must not turn into a hundred hand-overs in a row.",
  },
  scheduler_enabled: {
    ru: "Включён ли встроенный планировщик синхронизаций.",
    en: "Whether the built-in sync scheduler is running.",
  },
  syrve_repull_days: {
    ru: "Сколько последних дней перезабирать из Syrve при каждом прогоне: чеки правят и сторнируют задним числом.",
    en: "How many recent days to re-pull from Syrve on every run: receipts get edited and voided after the fact.",
  },
  banquet_booking_live_from: {
    ru: "Первая бронь в системе бронирования банкетов. До неё факта оплаты депозита не существует нигде.",
    en: "The first booking in the banquet booking system. Before it, a deposit payment is not recorded anywhere.",
  },
  base_currency: { ru: "Базовая валюта отчётности.", en: "The reporting base currency." },
  bulk_archival_days: {
    ru: "Разовая архивация ~70 тыс. лидов старой эпохи в стадию Historical lead. Не считать это движением воронки.",
    en: "A one-off archival of ~70k legacy leads into the Historical lead stage. Not to be counted as funnel movement.",
  },
  bulk_import_months: {
    ru: "Месяцы разовых заливок базы (исключаются из графиков объёма). Список через запятую.",
    en: "Months of one-off base imports (excluded from volume charts). Comma-separated.",
  },
  conversion_lead_to_deal: {
    ru: "Конверсия лид→сделка считается по наличию связанной сделки Banquet (LEAD_ID), а не по стадии CLOSED DEAL.",
    en: "Lead → deal conversion is counted by the presence of a linked Banquet deal (LEAD_ID), not by the CLOSED DEAL stage.",
  },
  currency_display_rule: {
    ru: "Базовая валюта AED. В сводных отчётах BHD пересчитывается в AED. Если фильтр выбирает только парки одной страны — показываем в её валюте.",
    en: "The base currency is AED. In combined reports BHD is converted to AED. When the filter selects parks of a single country, figures are shown in that country's currency.",
  },
  funnel_basis: {
    ru: "Воронка строится по факту прохождения стадии (история переходов), а не по текущей стадии лида. История есть с 01.07.2026.",
    en: "The funnel is built from stages actually passed (the transition history), not from a lead's current stage. History exists from 2026-07-01.",
  },
  known_issue_bhd_opportunity: {
    ru: "У сделок в BHD поле суммы не заполняется (значения 1.000/1.400), реальные деньги в Actual amount. Выручка Бахрейна по OPPORTUNITY занижена ~вдвое. Требует правки на стороне CRM.",
    en: "On BHD deals the amount field is not filled in (values like 1.000/1.400); the real money sits in Actual amount. Bahrain revenue by OPPORTUNITY is understated roughly twofold. Needs fixing on the CRM side.",
  },
  managed_era_from: {
    ru: "Граница эпох качества данных. До этой даты воронка в CRM не велась: 92–99 % лидов каждого месяца уходило в мусорные стадии.",
    en: "The data-quality era boundary. Before this date the CRM funnel was not maintained: 92–99% of each month's leads ended up in junk stages.",
  },
  online_tickets_in_syrve: {
    ru: "Билеты с нашего сайта попадают в Syrve как доставка с нулевой суммой и пометкой билета — так устроено намеренно из-за отношений с ТЦ. Выручка по ним берётся из БД Ticketing, из кассы — только визиты.",
    en: "Tickets from our own site land in Syrve as a zero-amount delivery marked as a ticket — deliberately, because of the mall arrangements. Their revenue is taken from the Ticketing database; the till only gives visits.",
  },
  park_revenue_source: {
    ru: "Выручка парка берётся из кассы Syrve. Выручку по сделкам CRM к ней НЕ прибавляем: банкетные деньги частично проходят через кассу, и сумма дала бы двойной счёт.",
    en: "Park revenue comes from the Syrve till. CRM deal revenue is NOT added to it: banquet money partly goes through the till, so adding them would double-count.",
  },
  revenue_basis: {
    ru: "Отчётная выручка считается по полю OPPORTUNITY, чтобы совпадать с Битриксом. Расхождения с Actual/Deposit видны в core.v_deal_amount_quality.",
    en: "Reported revenue is taken from the OPPORTUNITY field so it matches Bitrix. Divergences from Actual/Deposit are visible in core.v_deal_amount_quality.",
  },
  revenue_recognition: {
    ru: "Сделка в успешной стадии = завершённый банкет с выручкой (подтверждено заказчиком 23.08.2026). Выручка = OPPORTUNITY по таким сделкам.",
    en: "A deal in a won stage means a completed banquet with revenue (confirmed by the client on 2026-08-23). Revenue is the OPPORTUNITY of such deals.",
  },
  "syrve_backfill_from:ll_bahrain": {
    ru: "С какой даты добирать историю чеков по Бахрейну.",
    en: "The date from which to backfill Bahrain receipt history.",
  },
  syrve_history_from: {
    ru: "Самая ранняя дата чеков, найденная в RMS (Leo & Loona DFC Mall). По остальным паркам глубина неизвестна — нет прав.",
    en: "The earliest receipt date found in RMS (Leo & Loona DFC Mall). For the other parks the depth is unknown — no access.",
  },
  ticketing_live_from: {
    ru: "Первая бронь на сайте покупки билетов.",
    en: "The first booking on the ticket site.",
  },
  utm_fields_mandatory_in_portal: {
    ru: "Проверено по всем 32 UTM-полям портала (лид, сделка, контакт, компания): mandatory не стоит ни у одного. Снятие обязательности не является способом починки.",
    en: "Checked across all 32 UTM fields in the portal (lead, deal, contact, company): none is marked mandatory. Removing a mandatory flag is therefore not the fix.",
  },
  utm_policy: {
    ru: "Исторические UTM не нормализуем: пригодных для атрибуции ~300 лидов из 85 577. Ведём реестр сырых значений с классификацией и вытаскиваем идентификаторы кампаний Meta/Google.",
    en: "Historical UTMs are not normalised: only ~300 leads out of 85,577 carry anything usable for attribution. We keep a registry of raw values with a classification and extract Meta/Google campaign identifiers.",
  },
} satisfies Record<string, L>;
